// Field schemas for the CMS module editor. Types: text, textarea, bool, select, image, images, lines, items, object
const ICONS = ["Check", "Sun", "BadgeCheck", "ShieldCheck", "Frame", "ThermometerSun", "Award", "Eye", "Star", "Cog", "CloudRain", "Lock", "Ruler"];

export const MODULE_SCHEMAS = {
  hero: {
    label: "Hero (full-screen banner + enquiry form)",
    fields: [
      { k: "image", t: "image", label: "Background image" },
      { k: "alt", t: "text", label: "Image alt text (SEO)" },
      { k: "kicker", t: "text", label: "Small line above the title" },
      { k: "lines", t: "lines", label: "Title lines (one per row)" },
      { k: "subtitle", t: "textarea", label: "Subtitle" },
      { k: "brochure", t: "bool", label: "Show brochure button" },
      { k: "consultProduct", t: "text", label: "Enquiry form product name" },
      { k: "breadcrumb", t: "object", label: "Breadcrumb (optional)", fields: [
        { k: "label", t: "text", label: "Label" }, { k: "to", t: "text", label: "Link (e.g. /origin/)" }] },
    ],
    blank: { type: "hero", image: "", kicker: "", lines: ["New Page"], subtitle: "", consultProduct: "Windows & Doors" },
  },
  about: {
    label: "About (copy + badges + side image)",
    fields: [
      { k: "number", t: "text", label: "Section number (e.g. 01)" },
      { k: "kicker", t: "text", label: "Kicker" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "paragraphs", t: "lines", label: "Paragraphs (one per row)" },
      { k: "badges", t: "images", label: "Badge / logo images" },
      { k: "sideImage", t: "image", label: "Side image" },
      { k: "alt", t: "text", label: "Side image alt text (SEO)" },
      { k: "quickEnquiry", t: "bool", label: "Show quick enquiry link" },
    ],
    blank: { type: "about", number: "01", kicker: "", heading: "", paragraphs: [""], quickEnquiry: true },
  },
  rangeCards: {
    label: "Range cards (image cards linking to pages)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "cards", t: "items", label: "Cards", fields: [
        { k: "img", t: "image", label: "Image" }, { k: "title", t: "text", label: "Title" },
        { k: "copy", t: "textarea", label: "Copy" }, { k: "to", t: "text", label: "Link" }] },
    ],
    blank: { type: "rangeCards", number: "02", heading: "The range", cards: [] },
  },
  benefits: {
    label: "Benefits (icon grid)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "variant", t: "select", label: "Style", options: ["", "light"] },
      { k: "heading", t: "text", label: "Heading" },
      { k: "text", t: "textarea", label: "Intro text (optional)" },
      { k: "items", t: "items", label: "Benefits", fields: [
        { k: "icon", t: "select", label: "Icon", options: ICONS },
        { k: "title", t: "text", label: "Title" }, { k: "text", t: "textarea", label: "Text" }] },
    ],
    blank: { type: "benefits", number: "03", heading: "Key benefits", items: [] },
  },
  gallery: {
    label: "Gallery (masonry image grid)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "images", t: "images", label: "Images" },
    ],
    blank: { type: "gallery", number: "04", images: [] },
  },
  featureSplit: {
    label: "Feature split (image + copy side by side)",
    fields: [
      { k: "heading", t: "text", label: "Heading" },
      { k: "paragraphs", t: "lines", label: "Paragraphs" },
      { k: "image", t: "image", label: "Image" },
      { k: "alt", t: "text", label: "Image alt text (SEO)" },
      { k: "reverse", t: "bool", label: "Image on the right" },
    ],
    blank: { type: "featureSplit", heading: "", paragraphs: [""], image: "" },
  },
  caseStudy: {
    label: "Case study (video + copy)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "kicker", t: "text", label: "Kicker" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "text", t: "textarea", label: "Copy" },
      { k: "video", t: "text", label: "Video embed URL (YouTube/Streamable embed link)" },
      { k: "logo", t: "image", label: "Brand logo (optional)" },
    ],
    blank: { type: "caseStudy", number: "03", kicker: "Case study", heading: "", text: "", video: "" },
  },
  textBand: {
    label: "Text band (centered statement)",
    fields: [
      { k: "heading", t: "text", label: "Heading" },
      { k: "paragraphs", t: "lines", label: "Paragraphs" },
    ],
    blank: { type: "textBand", heading: "", paragraphs: [] },
  },
  related: {
    label: "Related links (chips at page end)",
    fields: [
      { k: "links", t: "items", label: "Links", fields: [
        { k: "label", t: "text", label: "Label" }, { k: "to", t: "text", label: "Link" }] },
    ],
    blank: { type: "related", links: [] },
  },
  iconCards: {
    label: "Icon cards (illustrated click-through cards)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "kicker", t: "text", label: "Kicker" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "text", t: "textarea", label: "Intro text" },
      { k: "items", t: "items", label: "Cards", fields: [
        { k: "icon", t: "image", label: "Icon image" }, { k: "title", t: "text", label: "Title" },
        { k: "d", t: "textarea", label: "Description" }, { k: "to", t: "text", label: "Link" }] },
    ],
    blank: { type: "iconCards", number: "02", heading: "", items: [] },
  },
  splitCta: {
    label: "Split CTA (image + copy + external button)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "kicker", t: "text", label: "Kicker" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "text", t: "textarea", label: "Copy" },
      { k: "image", t: "image", label: "Image" },
      { k: "alt", t: "text", label: "Image alt text (SEO)" },
      { k: "logo", t: "image", label: "Logo (optional)" },
      { k: "reverse", t: "bool", label: "Image on the right" },
      { k: "button", t: "object", label: "Button", fields: [
        { k: "label", t: "text", label: "Label" }, { k: "href", t: "text", label: "URL" }] },
    ],
    blank: { type: "splitCta", number: "03", heading: "", text: "", image: "" },
  },
  brandPanels: {
    label: "Brand panels (large logo tiles)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "panels", t: "items", label: "Panels", fields: [
        { k: "logo", t: "image", label: "Logo" }, { k: "alt", t: "text", label: "Alt text" },
        { k: "bg", t: "text", label: "Background colour (e.g. #2A363E)" }, { k: "to", t: "text", label: "Link" }] },
    ],
    blank: { type: "brandPanels", number: "02", panels: [] },
  },
  bento: {
    label: "Bento grid (asymmetric product tiles)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "kicker", t: "text", label: "Kicker" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "items", t: "items", label: "Tiles", fields: [
        { k: "img", t: "image", label: "Image" }, { k: "title", t: "text", label: "Title" },
        { k: "d", t: "textarea", label: "Description" }, { k: "tag", t: "text", label: "Tag" },
        { k: "to", t: "text", label: "Link (optional)" },
        { k: "span", t: "select", label: "Width", options: ["lg:col-span-4", "lg:col-span-5", "lg:col-span-6", "lg:col-span-7", "lg:col-span-8", "lg:col-span-12"] }] },
    ],
    blank: { type: "bento", number: "02", heading: "", items: [] },
  },
  fullImage: {
    label: "Full-width image",
    fields: [{ k: "image", t: "image", label: "Image" }, { k: "alt", t: "text", label: "Image alt text (SEO)" }],
    blank: { type: "fullImage", image: "" },
  },
  richText: {
    label: "Content section (heading + copy + images + video)",
    fields: [
      { k: "number", t: "text", label: "Section number (optional)" },
      { k: "kicker", t: "text", label: "Kicker (optional)" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "variant", t: "select", label: "Background", options: ["light", "dark", "navy"] },
      { k: "paragraphs", t: "lines", label: "Paragraphs (one per row)" },
      { k: "bullets", t: "lines", label: "Bullet points (one per row)" },
      { k: "images", t: "images", label: "Images (1-2 sit beside the copy, 3+ become a grid)" },
      { k: "video", t: "text", label: "Video embed URL (YouTube/Vimeo/Streamable embed link)" },
      { k: "button", t: "object", label: "Button (optional)", fields: [
        { k: "label", t: "text", label: "Label" }, { k: "to", t: "text", label: "Link (/path/ or https://…)" }] },
    ],
    blank: { type: "richText", heading: "", variant: "light", paragraphs: [""], bullets: [], images: [] },
  },
  faq: {
    label: "FAQ (accordion of questions & answers)",
    fields: [
      { k: "number", t: "text", label: "Section number" },
      { k: "kicker", t: "text", label: "Kicker" },
      { k: "heading", t: "text", label: "Heading" },
      { k: "variant", t: "select", label: "Style", options: ["", "light"] },
      { k: "items", t: "items", label: "Questions", fields: [
        { k: "q", t: "text", label: "Question" }, { k: "a", t: "textarea", label: "Answer" }] },
    ],
    blank: { type: "faq", number: "08", heading: "Frequently asked questions.", items: [{ q: "", a: "" }] },
  },
};

export const MODULE_TYPES = Object.keys(MODULE_SCHEMAS);
